// Pure Client-Side Image Service
// Downloads SharePoint images to browser cache and stores metadata for local paths

export interface ClientImageFile {
  id: string
  name: string
  localPath: string  // Local path like "/images/classes/filename.jpg"
  blobUrl: string    // Browser blob URL for immediate display
  category: string
  lastModified: string
}

class ClientImageService {
  private imageCache = new Map<string, ClientImageFile>()

  /**
   * Download image from SharePoint and convert to blob URL
   */
  async downloadImageAsBlob(imageUrl: string, imageName: string, category: string, originalId?: string): Promise<ClientImageFile | null> {
    try {
      console.log(`Downloading image: ${imageName}`)

      // Fetch the image
      const response = await fetch(imageUrl)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)

      // Create sanitized filename
      const sanitizedName = imageName
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/_{2,}/g, '_')
        .toLowerCase()

      // Create local path structure
      const localPath = `/images/${category}/${sanitizedName}`

      const imageFile: ClientImageFile = {
        id: originalId || this.generateImageId(imageName),
        name: sanitizedName,
        localPath: localPath,
        blobUrl: blobUrl,
        category: category,
        lastModified: new Date().toISOString()
      }

      // Cache the image
      this.imageCache.set(imageFile.id, imageFile)

      console.log(`✅ Downloaded and cached: ${localPath}`)
      return imageFile

    } catch (error) {
      console.error(`❌ Failed to download image ${imageName}:`, error)
      return null
    }
  }

  /**
   * Process SharePoint images and convert to client-side cached images
   */
  async processSharePointImages(sharepointImages: any[]): Promise<ClientImageFile[]> {
    const clientImages: ClientImageFile[] = []

    console.log(`Processing ${sharepointImages.length} SharePoint images client-side`)

    for (const spImage of sharepointImages) {
      const clientImage = await this.downloadImageAsBlob(
        spImage.downloadUrl,
        spImage.name,
        spImage.category,
        spImage.id  // Preserve original SharePoint ID
      )

      if (clientImage) {
        clientImages.push(clientImage)
      }
    }

    console.log(`✅ Successfully processed ${clientImages.length} images`)
    return clientImages
  }

  /**
   * Get image blob URL by ID
   */
  getImageBlobUrl(imageId: string): string | null {
    const image = this.imageCache.get(imageId)
    return image?.blobUrl || null
  }

  /**
   * Get image local path by ID (for JSON storage)
   */
  getImageLocalPath(imageId: string): string | null {
    const image = this.imageCache.get(imageId)
    return image?.localPath || null
  }

  /**
   * Get all cached images
   */
  getAllCachedImages(): ClientImageFile[] {
    return Array.from(this.imageCache.values())
  }

  /**
   * Clear image cache and revoke blob URLs
   */
  clearCache(): void {
    for (const image of this.imageCache.values()) {
      URL.revokeObjectURL(image.blobUrl)
    }
    this.imageCache.clear()
    console.log('Image cache cleared')
  }

  /**
   * Generate consistent image ID
   */
  private generateImageId(imageName: string): string {
    let hash = 0
    for (let i = 0; i < imageName.length; i++) {
      const char = imageName.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(36)
  }

  /**
   * Save images metadata to localStorage for persistence
   */
  saveImagesToStorage(): void {
    const imagesData = this.getAllCachedImages().map(img => ({
      id: img.id,
      name: img.name,
      dataUrl: img.blobUrl, // Use blob URL as dataUrl for public site
      category: img.category,
      lastModified: img.lastModified
    }))

    localStorage.setItem('salty_mermaid_images', JSON.stringify(imagesData))
    console.log(`💾 Saved ${imagesData.length} images to localStorage`)
  }

  /**
   * Restore images from localStorage (for page refresh)
   */
  loadImagesFromStorage(): void {
    try {
      const stored = localStorage.getItem('salty_mermaid_images')
      if (stored) {
        const imagesData = JSON.parse(stored)
        console.log(`📂 Loaded ${imagesData.length} images from localStorage`)
        // Note: Blob URLs don't persist across page refreshes
        // This mainly restores the metadata structure
      }
    } catch (error) {
      console.error('Error loading images from storage:', error)
    }
  }
}

export const clientImageService = new ClientImageService()